"use client"

import { useEffect } from "react"
import { trackGuideSectionView, trackGuideTroubleshootOpen } from "@/lib/tracking"
import {
  GUIDE_PROBLEMS,
  GUIDE_SECTIONS,
  type GuideProblem,
  type GuideSection,
} from "@/lib/funnel-property-enums"

/** How long a block must stay in the reading zone before it counts as seen. */
export const GUIDE_VIEW_DWELL_MS = 1000

/**
 * The reading zone: the top 65% of the viewport. A threshold on the element
 * itself would never be met by a section taller than the screen.
 */
const READING_ZONE_ROOT_MARGIN = "0px 0px -35% 0px"

function isSection(value: string | undefined): value is GuideSection {
  return GUIDE_SECTIONS.includes(value as GuideSection)
}

function isProblem(value: string | undefined): value is GuideProblem {
  return GUIDE_PROBLEMS.includes(value as GuideProblem)
}

/**
 * Reports which /guide sections and troubleshooting entries a visitor reads.
 *
 * Renders nothing and reads the server markup's `data-guide-section` and
 * `data-guide-problem` attributes after hydration, so the ISR output is the
 * same with or without it. A block counts once per page view, after it has
 * stayed in the reading zone for GUIDE_VIEW_DWELL_MS: a fast scroll past the
 * page is not a read. Values outside the closed sets in
 * lib/funnel-property-enums.ts are ignored.
 */
export default function GuideEngagementTracker() {
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return

    const timers = new Map<Element, ReturnType<typeof setTimeout>>()
    const seen = new WeakSet<Element>()

    const report = (el: HTMLElement) => {
      seen.add(el)
      observer.unobserve(el)
      const { guideSection, guideProblem } = el.dataset
      if (isSection(guideSection)) trackGuideSectionView(guideSection)
      else if (isProblem(guideProblem)) trackGuideTroubleshootOpen(guideProblem)
    }

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          const el = entry.target as HTMLElement
          if (seen.has(el)) continue
          if (entry.isIntersecting) {
            if (!timers.has(el)) timers.set(el, setTimeout(() => {
              timers.delete(el)
              report(el)
            }, GUIDE_VIEW_DWELL_MS))
          } else {
            clearTimeout(timers.get(el))
            timers.delete(el)
          }
        }
      },
      { rootMargin: READING_ZONE_ROOT_MARGIN },
    )

    document
      .querySelectorAll<HTMLElement>("[data-guide-section], [data-guide-problem]")
      .forEach(el => observer.observe(el))

    return () => {
      observer.disconnect()
      timers.forEach(clearTimeout)
    }
  }, [])

  return null
}
